import type { IdentityMismatchField, ResolveIdentityConflictRequest } from "@repo/shared/types";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  getPublicIdentityConflicts,
  getPublicSumsubVerificationPreviousAnswer,
  getPublicSumsubVerificationValue,
  resolvePublicIdentityConflict,
  savePublicSumsubVerification,
} from "@/lib/api/public-flow/sumsub-verification";
import { useSessionToken } from "@/lib/context/public-session-context";
import { sumsubValueFingerprint } from "@/lib/hooks/use-flow-instances/sumsub-value-fingerprint";
import { publicFlowCompletionQueryKey } from "./use-public-flow-completion";

const keys = {
  value: ["publicFlow", "sumsub-verification", "flow-instance-value"] as const,
  previousAnswer: ["publicFlow", "sumsub-verification", "previous-answer"] as const,
  identityConflicts: (valueFingerprint: string) =>
    ["publicFlow", "sumsub-verification", "identity-conflict", valueFingerprint] as const,
};

export function usePublicSumsubVerificationValue() {
  const token = useSessionToken();
  const { data, error, isLoading, refetch } = useQuery({
    queryKey: keys.value,
    queryFn: () => getPublicSumsubVerificationValue(token),
    enabled: !!token,
  });
  return {
    inputData: data?.inputData ?? null,
    calculatedData: data?.calculatedData ?? null,
    error,
    isLoading,
    refetch,
  };
}

export function usePublicSumsubVerificationPreviousAnswer() {
  const token = useSessionToken();
  const { data, isLoading } = useQuery({
    queryKey: keys.previousAnswer,
    queryFn: () => getPublicSumsubVerificationPreviousAnswer(token),
    enabled: !!token,
  });
  return { previousAnswer: data ?? null, isLoading };
}

export function useSavePublicSumsubVerification() {
  const token = useSessionToken();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: unknown) => savePublicSumsubVerification(token, data),
    onSuccess: (result) => {
      if (result.success && result.inputData !== undefined) {
        queryClient.setQueryData(keys.value, {
          inputData: result.inputData,
          calculatedData: result.calculatedData,
        });
      }
      queryClient.invalidateQueries({ queryKey: publicFlowCompletionQueryKey });
    },
  });
}

/**
 * QL-f — identity conflicts on the public page.
 *
 * Invalidates the flow-completion query on every answer: resolving the last
 * conflict is what makes the component complete, and the progress bar and
 * Submit gate read that query rather than this one.
 */
export function usePublicIdentityConflicts(calculatedData?: string | null) {
  const token = useSessionToken();
  const queryClient = useQueryClient();

  // Keyed on the saved result, so any writer refreshes it — see the advisor
  // hook for why this is a key rather than five invalidations.
  const key = keys.identityConflicts(sumsubValueFingerprint(calculatedData));

  const { data, isLoading } = useQuery({
    queryKey: key,
    queryFn: () => getPublicIdentityConflicts(token),
    enabled: !!token,
  });

  const resolve = useMutation({
    mutationFn: (payload: ResolveIdentityConflictRequest) =>
      resolvePublicIdentityConflict(token, payload),
    onSuccess: (result) => {
      queryClient.setQueryData(key, result);
      queryClient.invalidateQueries({ queryKey: publicFlowCompletionQueryKey });
    },
  });

  return {
    rows: data?.rows ?? [],
    unresolved: data?.unresolved ?? [],
    isLoading,
    resolve: async (field: IdentityMismatchField, decision: "update" | "dismiss") => {
      await resolve.mutateAsync({ field, decision });
    },
  };
}
